import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Edit, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

type PostCreateAuthProps = {
  applicationId: string
  subId: string | null
  clientKey: string
  secretData: {
    password: string
  }
  headers: object
  metadata: object | null
}

async function postCreateAuth(data: PostCreateAuthProps) {
  return api.backend.post('/application-auth', data)
}

export function useCreateApplicationAuth() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PostCreateAuthProps
  >({
    mutationKey: ['/application-auth/create'],
    mutationFn: postCreateAuth,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Edit,
          title: t(
            'applications.drawer_manage_auth.toast_create_success.title',
          ),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/application-auth/findOne'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('applications.drawer_manage_auth.toast_create_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('applications.drawer_manage_auth.toast_create_error.description'),
        },
      })
    },
  })

  const handleCreateAuth = async (data: PostCreateAuthProps) => {
    return mutateAsync(data)
  }

  return {
    handleCreateAuth,
    isCreatingAuth: isPending,
  }
}
